// DOM → PDF export, A4 multipage with standard margins.
//
// Ported from my-jarvis-dashboard-erez (MJOS-101). See that file for the
// design notes on why we slice per-page instead of using negative y-offsets.
//
// Defaults: A4 portrait, 12 mm margins. Transparent pixels in the source
// DOM fall back to white.

export async function downloadAsPdf(
  element: HTMLElement,
  filename: string,
) {
  const [{ toCanvas }, { default: jsPDF }] = await Promise.all([
    import("html-to-image"),
    import("jspdf"),
  ]);

  await document.fonts.ready;

  const fullCanvas = await toCanvas(element, {
    pixelRatio: 2,
    cacheBust: true,
    backgroundColor: "#FFFFFF",
  });
  const fullW = fullCanvas.width;
  const fullH = fullCanvas.height;

  // A4 portrait, millimetres.
  const PAGE_W = 210;
  const PAGE_H = 297;
  const MARGIN = 12;
  const printableW = PAGE_W - MARGIN * 2;
  const printableH = PAGE_H - MARGIN * 2;

  const canvasPxPerMm = fullW / printableW;
  const sliceHeightPx = Math.floor(printableH * canvasPxPerMm);

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  let yPx = 0;
  let firstPage = true;
  while (yPx < fullH) {
    if (!firstPage) pdf.addPage();
    firstPage = false;

    const remainingPx = fullH - yPx;
    const thisSlicePx = Math.min(sliceHeightPx, remainingPx);

    const slice = document.createElement("canvas");
    slice.width = fullW;
    slice.height = thisSlicePx;
    const ctx = slice.getContext("2d");
    if (!ctx) throw new Error("could not allocate slice canvas context");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(fullCanvas, 0, -yPx);

    const sliceDataUrl = slice.toDataURL("image/jpeg", 0.92);
    const sliceHeightMm = thisSlicePx / canvasPxPerMm;

    pdf.addImage(
      sliceDataUrl,
      "JPEG",
      MARGIN,
      MARGIN,
      printableW,
      sliceHeightMm,
      undefined,
      "FAST",
    );

    yPx += thisSlicePx;
  }

  pdf.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}
